import { useCallback, useEffect, useState } from 'react';

import { fetchEndpointResult, useEndpoint } from 'api';
import type { ActionButtonState } from 'components/ActionButton';
import type { ValidationError } from 'errors';
import { useFileUpload, useValueChangeEffect } from 'hooks';
import type {
	CreateCustomizationRequest,
	CustomizationPlacement,
	StoredFileResponse,
} from 'models/api';
import type { ProductPrice } from 'models/price';
import type { VariantId } from 'models/product';
import type { CustomizationId } from 'types';
import { ignorePromiseRejection, is } from 'utils/helpers';

export interface ExistingPlacement extends CustomizationPlacement {
	customizationId: string;
}

function mapCreateCustomizationRequestItems(
	printPlacements: CustomizationPlacement[] | undefined,
): CreateCustomizationRequest[] {
	return (
		printPlacements?.map((printPlacement) => ({
			placementId: printPlacement.id,
			optionKey: printPlacement.option?.key,
			printImageFilename: printPlacement.printImageFilename?.fileName,
		})) ?? []
	);
}

export default function useProductPrint({
	id,
	isActive,
}: {
	id: VariantId;
	isActive: boolean;
}) {
	const [print, setPrint] = useState<StoredFileResponse | undefined>(undefined);
	const [totalCost, setTotalCost] = useState<ProductPrice | undefined>(
		undefined,
	);
	const [currentPrintPlacements, setCurrentPrintPlacements] = useState<
		CustomizationPlacement[] | undefined
	>();
	const [currentCustomizationId, setCurrentCustomizationId] = useState<
		CustomizationId | undefined
	>();

	const [addPrintPlacementIsLoading, setAddPrintPlacementIsLoading] =
		useState(false);
	const [addPrintPlacementErrors, setAddPrintPlacementErrors] = useState<
		ValidationError | undefined
	>();
	const addPrintPlacementButtonState: ActionButtonState =
		addPrintPlacementIsLoading
			? 'loading'
			: addPrintPlacementErrors
				? 'failure'
				: 'success';

	// Get print customization for product with possible existing print placements
	const {
		data: productCustomization,
		error: productCustomizationError,
		isLoading: isLoadingProductCustomization,
	} = useEndpoint(
		{
			name: 'GetProductCustomization',
			pathParams: { variantId: id },
			queryParams: {
				showHistory: 'true',
			},
		},
		{
			isActive,
		},
	);

	const [isInitialLoading, setIsInitialLoading] = useState(isActive);

	useValueChangeEffect(isLoadingProductCustomization, () => {
		setIsInitialLoading(isLoadingProductCustomization);
	});

	// Get possible stored prints
	const { data: storedPrints, invalidateCache: invalidateStoredPrintsCache } =
		useEndpoint(
			{
				name: 'GetUserFileStorage',
				pathParams: { storageType: 'ProductCustomization' },
			},

			{
				isActive,
			},
		);

	// Print upload
	const {
		errors: fileUploadErrors,
		hasError: hasFileUploadErrors,
		isLoading: fileUploadIsLoading,
		uploadedFiles,
		uploadFile,
	} = useFileUpload({
		storageType: 'ProductCustomization',
	});

	useEffect(() => {
		if (uploadedFiles.length > 0) {
			setPrint(uploadedFiles.at(0));
			invalidateStoredPrintsCache();
		}
	}, [invalidateStoredPrintsCache, uploadedFiles]);

	const reusePrint = useCallback(
		(printId: string | undefined) => {
			const existingPrint = storedPrints?.find(
				(file) => file.fileName === printId,
			);
			if (existingPrint) {
				setPrint(existingPrint);
			}
		},
		[storedPrints],
	);

	const removePrint = useCallback(() => {
		setPrint(undefined);
	}, []);

	const requestPrintPlacementUpdate = useCallback(
		async (printPlacements: CreateCustomizationRequest[]) => {
			setAddPrintPlacementIsLoading(true);
			const res = await fetchEndpointResult({
				name: 'PostProductCustomization',
				pathParams: { variantId: id },
				body: printPlacements,
			});

			setAddPrintPlacementIsLoading(false);
			if (res.isFailure('ValidationError')) {
				setAddPrintPlacementErrors(res.error);
				return;
			}
			if (res.isSuccess()) {
				const { customization } = res.value ?? {};
				setCurrentPrintPlacements(customization?.placements);
				setTotalCost(customization?.price);
				setCurrentCustomizationId(customization?.id);
			}
		},
		[id],
	);

	const addPrintPlacement = useCallback(
		(printPlacement: CreateCustomizationRequest) => {
			ignorePromiseRejection(
				requestPrintPlacementUpdate([
					...mapCreateCustomizationRequestItems(currentPrintPlacements),
					printPlacement,
				]),
			);
		},
		[currentPrintPlacements, requestPrintPlacementUpdate],
	);

	const removePrintPlacement = useCallback(
		(placementId: string) => {
			ignorePromiseRejection(
				requestPrintPlacementUpdate(
					mapCreateCustomizationRequestItems(
						currentPrintPlacements?.filter(
							(placement) => placement.id !== placementId,
						),
					),
				),
			);
		},
		[currentPrintPlacements, requestPrintPlacementUpdate],
	);

	return {
		// it is still not complete clear if swr caches 204 responses
		// if it does there could be cases where this leads to a forever loading state
		// since the initial loading state is never updated because the request is not made and no loading state change
		// is triggered, and since the empty response is cached there is never any `productCustomization` data
		isInitialLoading:
			isInitialLoading && !productCustomization && !productCustomizationError,
		hasError: Boolean(productCustomizationError),
		uploadPrint: uploadFile,
		print,
		storedPrints,
		customizationId: currentCustomizationId,
		existingPrintPlacements: productCustomization?.customizationHistory
			?.flatMap((customization) =>
				customization.placements?.map((placement) => ({
					...placement,
					customizationId: customization.id,
				})),
			)
			.filter(is.truthy) satisfies ExistingPlacement[] | undefined,
		addPrintPlacementButtonState,
		addPrintPlacementErrors,
		printUploadButtonState: (fileUploadIsLoading
			? 'loading'
			: hasFileUploadErrors
				? 'failure'
				: 'success') as ActionButtonState,
		printUploadErrors: fileUploadErrors,
		addPrintPlacement,
		removePrintPlacement,
		reusePrint,
		removePrint,
		placements:
			productCustomization?.variantCustomization.printOnClothes.placements,
		printPlacements: currentPrintPlacements,
		totalCost,
	};
}
